import type { District } from '../plot.ts'
import { decals, look, row, woodsy } from '../kit.ts'

/** The heart of town: Blob Square, the café and the hat shop. Everyone starts here. */
export const oldtown: District = {
  id: 'oldtown',
  name: 'Old Town',
  ground: 'grass',
  build(p) {
    p.zone(10, 8, 16, 16, 'stone')
    p.zone(0, 14, 10, 3, 'stone'); p.zone(26, 14, 10, 3, 'stone'); p.zone(16, 0, 3, 8, 'stone'); p.zone(16, 24, 3, 8, 'stone')
    p.zone(4, 7, 1, 7, 'stone'); p.zone(7, 21, 3, 1, 'stone'); p.zone(30, 6, 1, 8, 'stone'); p.zone(30, 24, 1, 8, 'stone')
    p.prop(
      { kind: 'fountain', x: 16, y: 14 },
      { kind: 'lamppost', x: 15, y: 13 }, { kind: 'lamppost', x: 19, y: 13 }, { kind: 'lamppost', x: 15, y: 17 }, { kind: 'lamppost', x: 19, y: 17 },
      { kind: 'bench', x: 11, y: 9, face: 1 }, { kind: 'bench', x: 23, y: 9, face: -1 }, { kind: 'bench', x: 11, y: 22, face: 1 }, { kind: 'bench', x: 23, y: 22, face: -1 },
      { kind: 'flowers', x: 12, y: 11, color: '#ff8fab' }, { kind: 'flowers', x: 23, y: 11, color: '#ffd166' },
      { kind: 'flowers', x: 12, y: 19, color: '#b69cff' }, { kind: 'flowers', x: 23, y: 19, color: '#ff9e6b' },
      { kind: 'signpost', x: 14, y: 20, text: 'Blob Square' },
      { kind: 'building', x: 2, y: 2, w: 6, d: 5, door: 2, color: '#f6e7d4', roof: '#c9705a', text: 'Moonbean Café', enter: 'cafe' },
      { kind: 'building', x: 2, y: 19, w: 5, d: 5, rot: 1, door: 2, color: '#fff0f5', roof: '#ff8fab', text: 'Hat Shop', enter: 'hatshop' },
      { kind: 'building', x: 28, y: 2, w: 5, d: 4, door: 2, color: '#fff3d6', roof: '#e07a5f' },
      { kind: 'building', x: 28, y: 20, w: 5, d: 4, door: 2, color: '#e3f2e6', roof: '#5aa9c9' },
    )
    p.tram(20, 26)
    p.place({ id: 'square', name: 'Blob Square', blurb: 'The fountain, the benches, the gossip. Everyone starts here.', x: 17, y: 20, accent: '#6cc47f', r: 9 })
    p.scatter({ density: 0.05, pick: woodsy([['tree', 5], ['bush', 3], ['flowers', 1]]) })
    p.critter({ kind: 'pigeon', loop: [[11.5, 12.5], [14.5, 10.2], [13.4, 21.5], [11.3, 19.6]], speed: 0.5 })
    p.critter({ kind: 'pigeon', loop: [[21.5, 10.5], [24.4, 15.6], [21.6, 21.4]], speed: 0.45 })
    p.npc({
      id: 'npc-mayor', name: 'Mayor Pudding', role: 'Mayor', look: look('mayorpud', 0, 2, 0, 'tophat'), speed: 1.6,
      stops: [
        { x: 17, y: 20, wait: 9, emote: 'smug', say: -1 },
        { x: 13, y: 10, wait: 6 },
        { x: 11, y: 22, wait: 14, say: -1 },
        { x: 22, y: 10, wait: 7, emote: 'happy' },
        { x: 23, y: 22, wait: 10, say: -1 },
      ],
      lines: ['Welcome to Blobland!', 'Lovely hat. Very civic.', 'The fountain was my idea, you know.', 'Take the tram if your feet get tired.', 'Have you been to the canals? Breathtaking.'],
    })
    p.npc({
      id: 'npc-fern', name: 'Fern', role: 'Gardener', look: look('gardenfern', 4, 6, 2, 'sprout'), speed: 1.2,
      stops: [
        { x: 13, y: 12, wait: 10, emote: 'happy', say: -1 },
        { x: 22, y: 12, wait: 10 },
        { x: 22, y: 18, wait: 10, emote: 'love' },
        { x: 13, y: 18, wait: 10, say: -1 },
      ],
      lines: ['These flowers needed a drink.', 'Mind the petunias!', 'Plants like it when you say hi.', 'The cherry trees up at Sakura Hill are in bloom.'],
    })
    // The mail carrier walks the streets around Old Town, so stops sit just outside the plot.
    p.npc({
      id: 'npc-postie', name: 'Postie Pat', role: 'Mail carrier', look: look('postiepat', 3, 9, 3, 'beanie'), speed: 2,
      stops: [
        { x: -2, y: -2, wait: 4, say: -1 },
        { x: 37, y: -2, wait: 5, emote: 'happy' },
        { x: 37, y: 33, wait: 4, say: -1 },
        { x: -2, y: 33, wait: 5 },
      ],
      lines: ['Parcel for… nope, not you.', 'Rain or shine, the mail gets through.', 'Anyone seen a blob called Mochi?', 'Every street in town, every day.'],
    })

    // ─── Detail ───
    // A café terrace, market stalls on the square, string lights over the north street.
    p.prop(
      { kind: 'table', x: 7, y: 9 }, { kind: 'chair', x: 6, y: 9, face: 1, color: '#c9705a' }, { kind: 'chair', x: 8, y: 9, face: -1, color: '#c9705a' },
      { kind: 'table', x: 7, y: 11 }, { kind: 'chair', x: 6, y: 11, face: 1, color: '#c9705a' }, { kind: 'chair', x: 8, y: 11, face: -1, color: '#c9705a' },
      { kind: 'aboard', x: 5, y: 8, text: 'Coffee' }, { kind: 'bicycle', x: 3, y: 8, color: '#5aa9c9' },
      { kind: 'stall', x: 12, y: 8, color: '#ff8fab', text: 'Flowers' }, { kind: 'stall', x: 20, y: 8, color: '#ffd166', text: 'Fruit' },
      { kind: 'festoon', x: 15, y: 3, w: 5, d: 1 }, { kind: 'festoon', x: 15, y: 27, w: 5, d: 1 },
      { kind: 'well', x: 23, y: 27 }, { kind: 'birdbath', x: 12, y: 27 },
      ...row('fence', 27, 29, 8), ...row('fence', 31, 33, 8), ...row('fence', 27, 29, 26), ...row('fence', 31, 33, 26),
      { kind: 'mailbox', x: 32, y: 7 }, { kind: 'mailbox', x: 32, y: 25 },
      { kind: 'crate', x: 14, y: 8 }, { kind: 'barrel', x: 22, y: 8 },
    )
    p.scatter(decals(['clover', 'clover', 'leaves'], 0.07, ['grass']))
    p.scatter(decals(['crack', 'puddle'], 0.025, ['stone']))
    p.critter({ kind: 'tram', loop: [[-2, -2], [37, -2], [37, 33], [-2, 33]], speed: 2.4 })
    p.critter({ kind: 'pigeon', loop: [[18.5, 22.5], [21, 20.4], [19.5, 18.6]], speed: 0.4 })
    p.npc({
      id: 'npc-pebble', name: 'Pebble', role: 'Kid', look: look('kidpebble', 0, 11, 4, 'propeller'), speed: 2.7,
      stops: [{ x: 11, y: 12, wait: 2, emote: 'happy' }, { x: 24, y: 12, wait: 2 }, { x: 24, y: 20, wait: 3, say: -1 }, { x: 11, y: 19, wait: 2, emote: 'surprised' }],
      lines: ['Tag, you’re it!', 'Can’t catch me!', 'The tram goes all the way round!', 'I saw a golden blob in the maze!'],
    })
    p.npc({
      id: 'npc-granny', name: 'Granny Bun', role: 'Regular', look: look('grannybun', 0, 17, 1, 'beret'), speed: 0.7,
      stops: [{ x: 11, y: 9, wait: 60, emote: 'happy', say: -1 }, { x: 12, y: 9, wait: 40, nap: true }, { x: 7, y: 13, wait: 10 }],
      lines: ['Sit with me a while, dear.', 'I remember when this was all fields.', 'The pigeons and I have an understanding.', 'Zzz… oh! Hello.'],
    })
    p.npc({
      id: 'npc-florist', name: 'Rosa', role: 'Florist', look: look('rosaflor', 4, 23, 2, 'flower'), speed: 1,
      stops: [{ x: 12, y: 9, wait: 25, say: -1 }, { x: 14, y: 10, wait: 6, emote: 'love' }],
      lines: ['Fresh tulips, just in!', 'A flower for your hat?', 'Sunflowers from the farm today.'],
    })
  },
}
